import type { ResolvedProvider } from '@wrongstack/core';
import {
  ConfigError,
  type Config,
  type Logger,
  type ModelsRegistry,
  ProviderRegistry,
} from '@wrongstack/core';
import { buildProviderFactoriesFromRegistry, makeProviderFromConfig, withCatalogCapabilities } from '@wrongstack/providers';
import {
  fallbackCodexProviderModels,
  filterCurrentCodexModelIds,
  isCodexCatalogModel,
} from '../auth-menu/openai-codex-oauth.js';

export interface ProviderSetupResult {
  resolvedProvider: ResolvedProvider | undefined;
  provider: ReturnType<ProviderRegistry['create']>;
  providerRegistry: ProviderRegistry;
}

export async function setupProvider(params: {
  config: Config;
  modelsRegistry: ModelsRegistry;
  logger: Logger;
}): Promise<ProviderSetupResult> {
  const { config, modelsRegistry, logger } = params;

  // Resolve provider details from models.dev.
  const savedProviderCfg = config.providers?.[config.provider];
  let resolvedProvider = await modelsRegistry.getProvider(config.provider).catch(() => undefined);
  if (!resolvedProvider && savedProviderCfg?.type && savedProviderCfg.type !== config.provider) {
    resolvedProvider = await modelsRegistry
      .getProvider(savedProviderCfg.type)
      .catch(() => undefined);
  }
  if (!resolvedProvider) {
    if (savedProviderCfg?.family) {
      // Config-only provider not in the models.dev catalog — e.g. the OAuth
      // subscription families (openai-codex, anthropic-oauth, github-copilot)
      // or any user-defined provider with an explicit `family`. Synthesize a
      // ResolvedProvider from config so boot proceeds (the actual transport is
      // still built below via makeProviderFromConfig / the registry).
      // When the saved config carries no models but the family is one of
      // the OAuth/subscription wire families, seed with the canonical
      // model list so the provider shows up in pickers and the WebUI.
      const family = savedProviderCfg.family;
      const savedModels = savedProviderCfg.models;
      let models: Array<{ id: string; name: string }>;
      if (savedModels && savedModels.length > 0) {
        models = savedModels.map((m) => ({ id: m, name: m }));
      } else if (family === 'openai-codex') {
        // Resolve from the models.dev catalog: pick all models with
        // family=gpt-codex* under the `openai` provider. When the
        // catalog is unavailable, fall back to the documented defaults.
        const openaiProvider = await modelsRegistry.getProvider('openai').catch(() => undefined);
        if (openaiProvider) {
          const catalogById = new Map(
            openaiProvider.models
              .filter(isCodexCatalogModel)
              .map((m) => [m.id, { id: m.id, name: m.name }] as const),
          );
          const catalogModels = filterCurrentCodexModelIds(catalogById.keys())
            .map((id) => catalogById.get(id))
            .filter((m): m is { id: string; name: string } => Boolean(m));
          if (catalogModels.length > 0) {
            models = catalogModels;
          } else {
            models = fallbackCodexProviderModels();
          }
        } else {
          models = fallbackCodexProviderModels();
        }
      } else {
        models = [];
      }

      resolvedProvider = {
        id: config.provider,
        name: config.provider,
        family,
        apiBase: savedProviderCfg.baseUrl,
        envVars: savedProviderCfg.envVars ?? [],
        models,
        npm: undefined,
      };
    } else {
      logger.warn(
        `Provider "${config.provider}" not found in models.dev. Continuing with raw config.`,
      );
    }
  } else if (resolvedProvider.family === 'unsupported' && !savedProviderCfg?.family) {
    throw new ConfigError({
      message:
        `Provider "${config.provider}" uses an unsupported wire family (${resolvedProvider.npm}). ` +
        `Install a plugin to enable it, or pick a different provider.`,
      code: 'CONFIG_INVALID',
      context: { provider: config.provider, family: resolvedProvider.npm, kind: 'unsupported' },
    });
  }

  // Provider registry — populated dynamically from models.dev catalog.
  const providerRegistry = new ProviderRegistry();
  if (config.features.modelsRegistry) {
    try {
      const factories = await buildProviderFactoriesFromRegistry({
        registry: modelsRegistry,
        log: logger,
      });
      for (const f of factories) providerRegistry.register(f);
    } catch (err) {
      throw new ConfigError({
        message:
          `Failed to load models.dev registry: ${err instanceof Error ? err.message : err}\n` +
          `Try \`wstack models refresh\` once you have network access, or run with --no-features.`,
        code: 'CONFIG_INVALID',
        context: { phase: 'registry-build', provider: config.provider },
        cause: err,
      });
    }
  }

  // Provider instance — registry-driven by default, falls through to config-only.
  const providerConfig = config.providers?.[config.provider] ?? {
    type: config.provider,
    apiKey: config.apiKey,
    baseUrl: config.baseUrl,
  };
  let provider: ReturnType<ProviderRegistry['create']>;
  try {
    const cfgWithType = { ...providerConfig, type: config.provider };
    if (config.features.modelsRegistry && providerRegistry.has(config.provider)) {
      provider = providerRegistry.create(cfgWithType);
    } else {
      provider = makeProviderFromConfig(config.provider, cfgWithType);
    }
  } catch (err) {
    throw new ConfigError({
      message: `Failed to create provider: ${err instanceof Error ? err.message : err}`,
      code: 'CONFIG_INVALID',
      context: { phase: 'provider-create', provider: config.provider },
      cause: err,
    });
  }

  // Resolve per-model capabilities (maxOutput, maxContext, etc.) from the
  // models.dev catalog and overlay them on the provider's family baseline.
  // Without this step `provider.capabilities.maxOutput` stays at the
  // family default — and Chimera / other subagents would default to a
  // conservative 8K instead of the model's actual output ceiling.
  //
  // The overlay helper is exported from @wrongstack/providers so the
  // same resolution rules (customCaps → catalog → base) are shared with
  // any other caller that constructs a Provider post-init. Failures
  // inside are swallowed inside the helper — the family default stands
  // and agent-response's 8192 safety net covers the rare cases where
  // the catalog is unreachable.
  if (config.features.modelsRegistry) {
    provider = await withCatalogCapabilities(
      modelsRegistry,
      config.provider,
      provider,
      { ...providerConfig, type: config.provider, model: config.model },
      logger,
    );
  }

  return { resolvedProvider, provider, providerRegistry };
}
